import { PlatformPressable } from '@react-navigation/elements';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  Modal,
  Dimensions,
  View,
  TouchableOpacity,
  Pressable,
} from 'react-native';

import { Box, Text } from '@components/Restyle';
import { useModalPosition } from '@components/TaskList/useModalPosition';
import theme from '@themes/theme';

interface BaseButtonProps {
  buttonHeight?: number;
  mr?: keyof typeof theme.spacing;
  children?: ReactNode;
  noBorder?: boolean;
  noRadius?: boolean;
  disabled?: boolean;
  noHover?: boolean;
  onPress?: () => void;
  onModalShow?: (visible: boolean) => void;
  // about popup
  popModalComponent?: React.ComponentType<{ onClose: () => void }>;
  modalRadius?: number;
  buttonRadius?: keyof typeof theme.borderRadii;
  buttonBorderColor?: keyof typeof theme.colors;
  accessibilityLabel?: string;
  showHoverWhenDropdown?: boolean;
  /** Hover background (TA-9005). Defaults to the standard grey01; controls
   * sitting on a grey01-hovered row pass grey02 to stay visible. */
  hoverBackgroundColor?: keyof typeof theme.colors;
  /** Resting background. Defaults to white; row controls pass 'transparent'
   * so the row's own hover tint shows through. */
  restingBackgroundColor?: keyof typeof theme.colors;
}

type CreateTaskButtonProps = Pick<
  BaseButtonProps,
  'mr' | 'onPress' | 'accessibilityLabel'
>;

type ModalLayout = { width: number; height: number } | null;

export const BaseButtonV3: React.FC<BaseButtonProps> = ({
  mr = 'none',
  children,
  onPress,
  popModalComponent: PopModalComponent,
  onModalShow,
  modalRadius = 8,
  noBorder = false,
  noRadius = false,
  noHover = false,
  disabled = false,
  buttonRadius = 'm',
  buttonBorderColor = 'grey02',
  accessibilityLabel,
  showHoverWhenDropdown = false,
  hoverBackgroundColor = 'grey01',
  restingBackgroundColor = 'white',
  buttonHeight,
}) => {
  const [hovered, setHovered] = useState<boolean>(false);

  // Measure popup content size to decide whether it fits below the button.
  const [modalLayout, setModalLayout] = useState<ModalLayout>(null);

  const {
    ref,
    showModal,
    position: buttonPosition,
    openModal,
    closeModal,
  } = useModalPosition();

  const handleButtonPress = useCallback(() => {
    if (disabled) return;

    if (onPress) {
      onPress();
      return;
    }

    if (PopModalComponent) {
      // Reset layout when opening so we can re-measure next time.
      setModalLayout(null);
      onModalShow?.(true);
      openModal();
    }
  }, [disabled, onPress, PopModalComponent, openModal, onModalShow]);

  const handleCloseModal = useCallback(() => {
    onModalShow?.(false);
    closeModal();
  }, [closeModal, onModalShow]);

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  const bgColor = useMemo(() => {
    if (disabled) return 'grey02';
    return (hovered && !noHover) || (showHoverWhenDropdown && showModal)
      ? hoverBackgroundColor
      : restingBackgroundColor;
  }, [
    disabled,
    hovered,
    noHover,
    showHoverWhenDropdown,
    showModal,
    hoverBackgroundColor,
    restingBackgroundColor,
  ]);

  const borderColor = useMemo(() => {
    if (noBorder) return 'white';
    return disabled ? 'grey04' : buttonBorderColor;
  }, [noBorder, disabled, buttonBorderColor]);

  /**
   * Compute popup position:
   * - Right aligned to the button
   * - Prefer showing below the button
   * - If it doesn't fit below (based on measured popup height), show above
   */
  const computedModalStyle = useMemo(() => {
    if (!showModal || !buttonPosition) return null;

    const viewportW = window.innerWidth || Dimensions.get('window').width;
    const viewportH = window.innerHeight || Dimensions.get('window').height;

    const gap = 4;
    const margin = 8;

    // Default: show below the button
    let top = buttonPosition.y + buttonPosition.height + gap;

    // Right aligned: compute `right` based on viewport and button rect
    let right =
      viewportW - buttonPosition.x - buttonPosition.width + theme.spacing[mr];

    // If we already measured popup height, decide whether it fits below
    if (modalLayout?.height) {
      const bottomY = top + modalLayout.height;
      if (bottomY > viewportH - margin) {
        // Not enough space below -> place above the button
        top = buttonPosition.y - modalLayout.height - gap;
      }
    }

    // Clamp top inside viewport (best effort)
    if (typeof top === 'number') {
      if (top < margin) top = margin;

      if (modalLayout?.height) {
        const maxTop = viewportH - margin - modalLayout.height;
        if (top > maxTop) top = Math.max(margin, maxTop);
      }
    }

    // Clamp right so it won't go off-screen (best effort)
    // (We need modal width for perfect clamp; with width measured it's accurate.)
    if (modalLayout?.width) {
      // Convert `right` to `left` for clamping, then convert back to `right`
      const left = viewportW - right - modalLayout.width;
      let clampedLeft = left;

      if (clampedLeft < margin) clampedLeft = margin;
      if (clampedLeft + modalLayout.width > viewportW - margin) {
        clampedLeft = viewportW - margin - modalLayout.width;
      }

      right = viewportW - clampedLeft - modalLayout.width;
    } else {
      // Without width, just keep the existing right-aligned logic.
      // (Still fine in most cases.)
    }

    return { top, right };
  }, [showModal, buttonPosition, modalLayout, mr]);

  const renderModalContent = () => {
    if (!showModal || !buttonPosition || !computedModalStyle) return null;

    return (
      <View
        style={{
          position: 'absolute',
          top: computedModalStyle.top,
          right: computedModalStyle.right,
          backgroundColor: 'white',
          borderRadius: modalRadius,
          shadowColor: 'black',
          shadowOpacity: 0.1,
          shadowOffset: { width: 0, height: 5 },
          shadowRadius: 25,
          minWidth: 100,
          minHeight: 40,
          zIndex: 999,
        }}
        // Measure popup size once it renders.
        onLayout={(e: any) => {
          const { width, height } = e?.nativeEvent?.layout || {};
          if (!width || !height) return;

          setModalLayout((curr) => {
            // Avoid state churn when size doesn't change
            if (curr && curr.width === width && curr.height === height)
              return curr;
            return { width, height };
          });
        }}
        // Right click inside popup: close (keep your current behavior)
        onContextMenu={(e: any) => {
          e.preventDefault?.();
          e.stopPropagation?.();
          handleCloseModal();
        }}
        // Prevent click events from bubbling to the overlay
        onClick={(e: any) => {
          e.preventDefault?.();
          e.stopPropagation?.();
        }}
        onMouseDown={(e: any) => {
          e.preventDefault?.();
          e.stopPropagation?.();
        }}>
        <Pressable style={{ flex: 1 }} onStartShouldSetResponder={() => true}>
          {PopModalComponent && (
            <PopModalComponent onClose={handleCloseModal} />
          )}
        </Pressable>
      </View>
    );
  };

  return (
    <>
      {!noHover && (
        <PlatformPressable
          ref={ref}
          accessibilityLabel={accessibilityLabel}
          onPress={handleButtonPress}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onHoverIn={handleMouseEnter}
          onHoverOut={handleMouseLeave}>
          <Box
            height={buttonHeight ?? 32}
            mr={mr}
            minWidth={32}
            borderRadius={noRadius ? 'none' : buttonRadius}
            borderWidth={noBorder ? 0 : 1}
            alignItems='center'
            justifyContent='center'
            flexDirection='row'
            borderColor={borderColor as any}
            backgroundColor={bgColor as any}>
            {children}
          </Box>
        </PlatformPressable>
      )}

      {noHover && (
        <TouchableOpacity
          accessibilityLabel={accessibilityLabel}
          onPress={handleButtonPress}
          ref={ref as any}>
          <Box
            height={buttonHeight ?? 32}
            mr={mr}
            minWidth={32}
            alignItems='center'
            justifyContent='center'
            flexDirection='row'>
            {children}
          </Box>
        </TouchableOpacity>
      )}

      {PopModalComponent && (
        <Modal
          visible={showModal}
          transparent={true}
          animationType='fade'
          onRequestClose={handleCloseModal}
          key={`modal-${showModal}`}>
          <View style={{ flex: 1 }}>
            {/* Overlay: click outside closes the popup */}
            <Pressable
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'transparent',
              }}
              onPress={handleCloseModal}
              // Right-click / trackpad right-click outside also closes
              onContextMenu={(e: any) => {
                e.preventDefault?.();
                e.stopPropagation?.();
                handleCloseModal();
              }}
              onMouseDown={(e: any) => {
                if (e?.button === 2) {
                  e.preventDefault?.();
                  e.stopPropagation?.();
                  handleCloseModal();
                }
              }}
              onPointerDown={(e: any) => {
                if (e?.button === 2) {
                  e.preventDefault?.();
                  e.stopPropagation?.();
                  handleCloseModal();
                }
              }}
            />

            {renderModalContent()}
          </View>
        </Modal>
      )}
    </>
  );
};

export const CreateTaskButton: React.FC<CreateTaskButtonProps> = ({
  mr = 'none',
  onPress,
  accessibilityLabel,
}) => {
  const handleButtonPress = useCallback(() => {
    if (onPress) {
      onPress();
    }
  }, [onPress]);

  return (
    <PlatformPressable
      accessibilityLabel={accessibilityLabel}
      onPress={handleButtonPress}>
      <Box
        height={32}
        width={125}
        mr={mr}
        minWidth={32}
        borderRadius='m'
        alignItems='center'
        justifyContent='center'
        flexDirection='row'
        backgroundColor='greenSecondary'>
        <Text
          accessibilityLabel='Create Task'
          variant='webBodyHintSubline'
          color='white'>
          Create Task
        </Text>
      </Box>
    </PlatformPressable>
  );
};
